import { getToolName, isToolUIPart, type UIMessage } from "ai";

export type DraftLegalRoleProposalInput = {
  name?: string;
  role_key?: string;
  instructions?: string;
  id?: string;
};

export type ToolCallApprovalPayload = {
  runId: string;
  toolCallId: string;
  toolName: string;
  args: DraftLegalRoleProposalInput;
};

export type DraftLegalRoleToolOutput = {
  success?: boolean;
  draftLegalRole?: {
    id: string;
    name: string;
    role_key: string;
    instructions?: string;
  };
  error?: string;
};

export const WRITE_TOOL_NAMES = new Set([
  "createDraftLegalRole",
  "updateDraftLegalRole",
  "create-draft-legal-role",
  "update-draft-legal-role",
]);

export function isWriteToolName(toolName: string): boolean {
  return WRITE_TOOL_NAMES.has(toolName);
}

export function findSuccessfulDraftLegalRoleWrite(
  messages: UIMessage[]
): { toolCallId: string; draftLegalRoleId: string; name: string } | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== "assistant") continue;
    for (let j = message.parts.length - 1; j >= 0; j--) {
      const part = message.parts[j];
      if (!isToolUIPart(part)) continue;
      if (!isWriteToolName(getToolName(part))) continue;
      if (part.state !== "output-available") continue;
      const output = part.output as DraftLegalRoleToolOutput | undefined;
      if (output?.success !== true || !output.draftLegalRole?.id) continue;
      return {
        toolCallId: part.toolCallId,
        draftLegalRoleId: output.draftLegalRole.id,
        name: output.draftLegalRole.name ?? "",
      };
    }
  }
  return null;
}

export function normalizeProposalInput(raw: unknown): DraftLegalRoleProposalInput {
  if (!raw || typeof raw !== "object") return {};
  const o = raw as Record<string, unknown>;
  const roleKey =
    typeof o.role_key === "string"
      ? o.role_key
      : typeof o.roleKey === "string"
        ? o.roleKey
        : undefined;
  return {
    id: typeof o.id === "string" ? o.id : undefined,
    name: typeof o.name === "string" ? o.name : undefined,
    role_key: roleKey,
    instructions: typeof o.instructions === "string" ? o.instructions : undefined,
  };
}
